import { useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Button } from '../../components/ui/Button';
import { PhoneInput, isValidUSPhone } from '../../components/ui/PhoneInput';
import { useCreateClient } from '../../hooks/useClients';
import { useAuthStore } from '../../stores/authStore';
import { getLocations } from '../../services/api/locationService';
import userService from '../../services/api/userService';
import {
  ClientForm,
  ClientFieldRow,
  ClientFormError,
  Textarea,
  FieldLabel,
  FieldGroup,
} from './ClientFormLayout';
import { GENDER_OPTIONS } from './clientFormConstants';
import type { CreateClientDto } from '../../types/clients';

interface CreateClientFormValues {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: string;
  phone: string;
  email: string;
  externalId: string;
  notes: string;
  primaryCenterId: string;
  assigneeId: string;
}

interface CreateClientModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CreateClientModal = ({ isOpen, onClose }: CreateClientModalProps) => {
  const { hasPermission } = useAuthStore();
  const canCreate = hasPermission('clients:manage');
  const createClient = useCreateClient();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm<CreateClientFormValues>({
    defaultValues: {
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      gender: '',
      phone: '',
      email: '',
      externalId: '',
      notes: '',
      primaryCenterId: '',
      assigneeId: '',
    },
  });

  const { data: locationsData } = useQuery({
    queryKey: ['locations', 'all-for-clients'],
    queryFn: () => getLocations({ limit: 200 }),
    enabled: isOpen,
  });

  const { data: membersData } = useQuery({
    queryKey: ['members', 'all-for-clients'],
    queryFn: () => userService.getMembers({ limit: 200 }),
    enabled: isOpen,
  });

  const centerOptions = useMemo(() => [
    { value: '', label: 'Select center...' },
    ...(locationsData?.data ?? []).map((loc) => ({ value: loc.id, label: loc.name })),
  ], [locationsData]);

  const memberOptions = useMemo(() => [
    { value: '', label: 'Select BCBA...' },
    ...(membersData?.data ?? [])
      .filter((m) => m.isActive)
      .map((m) => ({ value: m.id, label: `${m.firstName} ${m.lastName}` })),
  ], [membersData]);

  const onSubmit = (values: CreateClientFormValues) => {
    if (!canCreate) return;
    const dto: CreateClientDto = {
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      ...(values.dateOfBirth ? { dateOfBirth: values.dateOfBirth } : {}),
      ...(values.gender ? { gender: values.gender } : {}),
      ...(values.phone ? { phone: values.phone.trim() } : {}),
      ...(values.email ? { email: values.email.trim() } : {}),
      ...(values.externalId ? { externalId: values.externalId.trim() } : {}),
      ...(values.notes ? { notes: values.notes.trim() } : {}),
      ...(values.primaryCenterId ? { primaryCenterId: values.primaryCenterId } : {}),
      ...(values.assigneeId ? { assigneeId: values.assigneeId } : {}),
    };
    createClient.mutate(dto, { onSuccess: handleClose });
  };

  const handleClose = () => {
    reset();
    createClient.reset();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add Client"
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={createClient.isPending}>
            Cancel
          </Button>
          {canCreate && (
            <Button
              variant="primary-blue"
              onClick={handleSubmit(onSubmit)}
              isLoading={createClient.isPending}
            >
              Add Client
            </Button>
          )}
        </>
      }
    >
      <ClientForm onSubmit={handleSubmit(onSubmit)}>
        {createClient.isError && (
          <ClientFormError>
            {(createClient.error as any)?.response?.data?.message ||
              'Failed to add client. Please try again.'}
          </ClientFormError>
        )}

        <ClientFieldRow>
          <Input
            {...register('firstName', { required: 'First name is required' })}
            label="First Name"
            placeholder="e.g. John"
            error={errors.firstName?.message}
            required
            fullWidth
          />
          <Input
            {...register('lastName', { required: 'Last name is required' })}
            label="Last Name"
            placeholder="e.g. Doe"
            error={errors.lastName?.message}
            required
            fullWidth
          />
        </ClientFieldRow>

        <ClientFieldRow>
          <Input
            {...register('dateOfBirth')}
            label="Date of Birth"
            type="date"
            fullWidth
          />
          <Controller
            name="gender"
            control={control}
            render={({ field }) => (
              <Select
                label="Gender"
                options={GENDER_OPTIONS}
                value={field.value}
                onChange={field.onChange}
                fullWidth
              />
            )}
          />
        </ClientFieldRow>

        <ClientFieldRow>
          <Controller
            name="phone"
            control={control}
            rules={{ validate: (v: string) => isValidUSPhone(v) || 'Enter a valid 10-digit US phone number' }}
            render={({ field, fieldState }) => (
              <PhoneInput
                label="Phone"
                fullWidth
                value={field.value}
                error={fieldState.error?.message}
                onChange={field.onChange}
              />
            )}
          />
          <Input
            {...register('email')}
            label="Email"
            type="email"
            placeholder="e.g. client@example.com"
            fullWidth
          />
        </ClientFieldRow>

        <Input
          {...register('externalId')}
          label="External ID / MRN"
          placeholder="e.g. MRN-12345"
          fullWidth
        />

        <ClientFieldRow>
          <Controller
            name="primaryCenterId"
            control={control}
            render={({ field }) => (
              <Select
                label="Primary Center"
                options={centerOptions}
                value={field.value}
                onChange={field.onChange}
                fullWidth
              />
            )}
          />
          <Controller
            name="assigneeId"
            control={control}
            render={({ field }) => (
              <Select
                label="Assigned BCBA"
                options={memberOptions}
                value={field.value}
                onChange={field.onChange}
                fullWidth
              />
            )}
          />
        </ClientFieldRow>

        <FieldGroup>
          <FieldLabel htmlFor="client-notes">Notes</FieldLabel>
          <Textarea
            id="client-notes"
            {...register('notes')}
            placeholder="Optional notes about this client..."
          />
        </FieldGroup>
      </ClientForm>
    </Modal>
  );
};
